import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePolling } from "@/hooks/usePolling";
import { QueueDepthBarChart } from "../charts/QueueDepthBarChart";
import { QueueDepthHistoryChart } from "../charts/QueueDepthHistoryChart";
import { ScrapingThroughputChart } from "../charts/ScrapingThroughputChart";
import { TaskFlowDiagram } from "../charts/TaskFlowDiagram";
import { FilesSection } from "../components/FilesSection";
import { ScrapingDailyAreaChart } from "@/components/charts/ScrapingDailyAreaChart";
import { buildDailyChartData } from "@/lib/scrapingEvents";
import {
  getScrapingDailyStats,
  getScrapingThroughput,
  DailyStatRow,
  ThroughputPoint,
} from "@/services/scrapingStatsApi";
import {
  getQueueHistory,
  getInflightByWorker,
  QueueHistoryPoint,
  QueueSummaryResponse,
  InflightByWorkerResponse,
} from "@/services/workerApi";
import { PluginStatus } from "@/services/pluginStatusApi";

// Vue d'ensemble = FLUX des tâches : profondeur instantanée des files (live),
// tendance de profondeur (historique snapshots), et débit de scraping dans le
// temps (historique Postgres).
export function OverviewTab({
  summary,
  plugins,
  navigate,
  live,
  onMutated,
}: {
  summary: QueueSummaryResponse | null;
  plugins: PluginStatus[] | null;
  navigate: NavigateFunction;
  live: boolean;
  onMutated: () => void;
}) {
  const [days, setDays] = useState(30);
  const [daily, setDaily] = useState<DailyStatRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [history, setHistory] = useState<QueueHistoryPoint[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [inflight, setInflight] = useState<InflightByWorkerResponse | null>(null);

  // Débit de scraping temps réel (rolling-60s). Fenêtre sélectionnable ; la
  // cadence de poll = résolution de la donnée (10s ≤1h, 30s ≤3h, 60s 6h) pour
  // un ressenti « live » sans marteler le backoffice.
  const [windowSec, setWindowSec] = useState(3600);
  const [throughput, setThroughput] = useState<ThroughputPoint[]>([]);
  const [loadingThroughput, setLoadingThroughput] = useState(true);
  const throughputStepMs = windowSec <= 3600 ? 10000 : windowSec <= 10800 ? 30000 : 60000;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await getScrapingDailyStats(navigate, { days });
        if (!cancelled) setDaily(r.data);
      } catch {
        // non bloquant : on garde les dernières données connues
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days, navigate]);

  // Historique de profondeur : snapshots écrits 1/min par le reaper. Polling
  // lent (30s) tant que l'onglet est ouvert et Live actif.
  const fetchHistory = useCallback(async () => {
    try {
      const r = await getQueueHistory(navigate, { windowSeconds: 3600 });
      setHistory(r.points);
    } catch {
      // non bloquant
    } finally {
      setLoadingHistory(false);
    }
  }, [navigate]);

  usePolling(fetchHistory, 30000, { enabled: live });

  // Tâches in-flight par worker (live) → détail des actions par plugin du flux.
  const fetchInflight = useCallback(async () => {
    try {
      const r = await getInflightByWorker(navigate);
      setInflight(r);
    } catch {
      // non bloquant : on garde la dernière photo connue
    }
  }, [navigate]);

  usePolling(fetchInflight, 5000, { enabled: live });

  // Débit temps réel. catch silencieux → on garde la dernière courbe en cas
  // d'erreur (comme fetchHistory / fetchInflight).
  const fetchThroughput = useCallback(async () => {
    try {
      const r = await getScrapingThroughput(navigate, { window: windowSec });
      setThroughput(r.points);
    } catch {
      // non bloquant
    } finally {
      setLoadingThroughput(false);
    }
  }, [navigate, windowSec]);

  usePolling(fetchThroughput, throughputStepMs, { enabled: live });

  // Refetch immédiat au changement de fenêtre. usePolling ne refait un appel
  // immédiat que si l'intervalle change (1h↔3h↔6h) ; 30min↔1h gardent 10s, donc
  // sans ça la nouvelle fenêtre tarderait jusqu'à 10s. Skip du 1er run (le
  // montage est déjà couvert par usePolling immediate).
  const throughputMounted = useRef(false);
  useEffect(() => {
    if (!throughputMounted.current) {
      throughputMounted.current = true;
      return;
    }
    void fetchThroughput();
  }, [fetchThroughput]);

  const chartData = useMemo(() => buildDailyChartData(daily), [daily]);

  const lastRate = throughput.length ? throughput[throughput.length - 1].total : 0;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Flux des tâches (temps réel)</CardTitle>
        </CardHeader>
        <CardContent>
          <TaskFlowDiagram summary={summary} plugins={plugins} inflight={inflight} />
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Profondeur des files (temps réel)</CardTitle>
          </CardHeader>
          <CardContent>
            <QueueDepthBarChart summary={summary} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">Débit de scraping par jour</CardTitle>
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="h-8 w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 derniers jours</SelectItem>
                <SelectItem value="30">30 derniers jours</SelectItem>
                <SelectItem value="90">90 derniers jours</SelectItem>
                <SelectItem value="365">12 derniers mois</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <ScrapingDailyAreaChart chartData={chartData} loading={loading} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Tendance de profondeur des files (dernière heure)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <QueueDepthHistoryChart points={history} loading={loadingHistory} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium">Débit de scraping en temps réel</CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{lastRate} / min</span>
            <Select value={String(windowSec)} onValueChange={(v) => setWindowSec(Number(v))}>
              <SelectTrigger className="h-8 w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1800">30 min</SelectItem>
                <SelectItem value="3600">1 h</SelectItem>
                <SelectItem value="10800">3 h</SelectItem>
                <SelectItem value="21600">6 h</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ScrapingThroughputChart points={throughput} loading={loadingThroughput} />
        </CardContent>
      </Card>

      <FilesSection summary={summary} live={live} navigate={navigate} onMutated={onMutated} />
    </div>
  );
}
